import React, { useRef, useEffect } from "react";
import SignaturePad from "react-signature-canvas";
import "./SignatureDrawing.css";
import TauriImage from "../../../../TauriImage/TauriImage";

const SignatureDrawing = ({
    onSignatureChange,
    Signature,
    textOverlay,
    overlayPosition = "bottom"
}) => {
    const sigPadRef = useRef();
    const overlayAppliedRef = useRef(false);

    const drawText = (ctx, text, canvas, position) => {
        const padding = 16;
        const bottomMargin = 26;

        const base = canvas.width / 32;
        const fontSize = Math.min(Math.max(base, 10), 14);

        ctx.save();
        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = "#1D5D9B";

        const maxWidth = canvas.width - padding * 2;
        const words = text.split(" ");

        let line = "";
        const lines = [];

        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + " ";
            const testWidth = ctx.measureText(testLine).width;

            if (testWidth > maxWidth && n > 0) {
                lines.push(line.trim());
                line = words[n] + " ";
            } else {
                line = testLine;
            }
        }
        lines.push(line.trim());

        const lineHeight = fontSize + 4;
        const blockHeight = lines.length * lineHeight;

        let startY = canvas.height - bottomMargin - blockHeight + lineHeight;

        if (position === "top") startY = padding + lineHeight;
        if (position === "center") startY = canvas.height / 2 - blockHeight / 2 + lineHeight;

        lines.forEach((lineText, i) => {
            const textWidth = ctx.measureText(lineText).width;
            const x = (canvas.width - textWidth) / 2;
            const y = startY + i * lineHeight;
            ctx.fillText(lineText, x, y);
        });

        ctx.restore();
    };

    const exportingRef = useRef(false);

    const handleEnd = async () => {
        if (!sigPadRef.current || sigPadRef.current.isEmpty()) return;

        if (exportingRef.current) return;
        exportingRef.current = true;

        try {
            const canvas = sigPadRef.current.getCanvas();

            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;

            const ctx = tempCanvas.getContext("2d");

            ctx.drawImage(canvas, 0, 0);

            if (textOverlay) {
                drawText(ctx, textOverlay, tempCanvas, overlayPosition);
            }

            const blob = await new Promise(resolve =>
                tempCanvas.toBlob(resolve, "image/png", 1)
            );

            onSignatureChange(blob);
        } finally {
            setTimeout(() => {
                exportingRef.current = false;
            }, 250);
        }
    };

    const clearSignature = () => {
        sigPadRef.current?.clear();

        resizeCanvas(); 
        overlayAppliedRef.current = false;

        hasLoadedRef.current = false;
        exportingRef.current = false;
        onSignatureChange(null);
    };

    const resizeCanvas = () => {
        if (!sigPadRef.current) return;

        const canvas = sigPadRef.current.getCanvas();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        const rect = canvas.getBoundingClientRect();

        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;

        canvas.getContext("2d").scale(ratio, ratio);

        sigPadRef.current.clear(); // prevent stretched strokes
    };

    const hasLoadedRef = useRef(false);

    useEffect(() => {
        if (!Signature || !sigPadRef.current || hasLoadedRef.current) return;

        hasLoadedRef.current = true;

        if (typeof Signature === "string") {
            sigPadRef.current.fromDataURL(Signature);
        } else if (Signature instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => sigPadRef.current.fromDataURL(reader.result);
            reader.readAsDataURL(Signature);
        }
    }, [Signature]);

    return (
        <div className="signature-pad">
            <SignaturePad
                ref={sigPadRef}
                canvasProps={{
                    className: "signature-canvas",
                    style: { touchAction: "none" }
                }}
                onEnd={handleEnd}
            />

            <TauriImage
                className="btn-clear"
                localImage="/Images/clear.svg"
                color="#389EE7"
                onClick={clearSignature}
            />
        </div>
    );
};

export default SignatureDrawing;